import { motion, AnimatePresence } from 'framer-motion'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import TermsOfServiceModal from '@/components/common/TermsOfService'
import PrivacyPolicyModal from '@/components/common/PrivacyPolicy'
import authService from '../services/authService'
import beemiLogo from '@/assets/icons/beemi-logo.png'
import { analyticsService } from '../services/analyticsService'

interface AuthPageProps {
  onLogin: () => Promise<void> | void
}

const AuthPage = ({ onLogin }: AuthPageProps) => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [showPrivacyModal, setShowPrivacyModal] = useState(false)

  // Google Client ID - using the same one configured in beemi-server
  const getGoogleClientId = () => {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) {
    throw new Error('VITE_GOOGLE_CLIENT_ID environment variable is not set. Please configure your environment properly.');
  }
  return clientId;
};

const GOOGLE_CLIENT_ID = getGoogleClientId();
const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID;

  useEffect(() => {
    // Check if Google and Apple scripts are loaded
    const checkSdksLoaded = () => {
      const googleLoaded = typeof window !== 'undefined' && window.google;
      const appleLoaded = typeof window !== 'undefined' && window.AppleID;
      
      if (googleLoaded) {
        setIsGoogleLoaded(true);
        authService.initializeGoogleAuth(GOOGLE_CLIENT_ID);
      }
      
      if (appleLoaded && APPLE_CLIENT_ID) {
        authService.initializeAppleAuth(APPLE_CLIENT_ID);
      }
      
      if (!googleLoaded || !appleLoaded) {
        setTimeout(checkSdksLoaded, 100);
      }
    };
    checkSdksLoaded();
  }, [])

  const handleGoogleSignIn = async (e?: React.MouseEvent) => {
    // Prevent any default action that might cause page reload
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('🔘 User clicked "Continue with Google" button');
    setIsLoading(true)
    setError(null)
    
    try {
      console.log('⏳ Setting loading state and calling authService.googleSignIn()');
      const response = await authService.googleSignIn()
      
      console.log('📥 Authentication response received:', response);
      
      if (response.success) {
        console.log('🎉 Authentication successful! Redirecting to dashboard...');
        console.log('👤 Authenticated user:', response.user);
        
        // Track authentication event
        analyticsService.track({
          name: response.isNewUser ? 'user_signed_up' : 'user_logged_in',
          properties: {
            auth_provider: 'google',
            is_new_user: response.isNewUser || false,
            source: 'web',
            login_method: 'oauth'
          }
        });
        
        await onLogin() // Update auth state
        navigate('/') // Navigate to dashboard
      } else {
        console.error('❌ Authentication failed:', response.message);
        
        // Track auth failure
        analyticsService.track({
          name: 'auth_failed',
          properties: {
            auth_provider: 'google',
            error_type: response.code || 'unknown',
            error_message: response.message
          }
        });
        
        setError(response.message || 'Authentication failed')
      }
    } catch (err: any) {
      console.error('💥 Google sign-in error:', err)
      // Extract the actual error message from the error object
      const errorMessage = err?.message || 'Failed to sign in with Google. Please try again.'
      
      // Track auth error
      analyticsService.track({
        name: 'auth_failed',
        properties: {
          auth_provider: 'google',
          error_type: 'exception',
          error_message: errorMessage
        }
      });
      
      setError(errorMessage)
    } finally {
      console.log('🔄 Clearing loading state');
      setIsLoading(false)
    }
  }

  const handleAppleSignIn = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('🍎 User clicked "Continue with Apple" button');
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('⏳ Calling authService.appleSignIn()');
      const response = await authService.appleSignIn();
      
      console.log('📥 Authentication response:', response);
      
      if (response.success) {
        console.log('🎉 Apple authentication successful!');
        console.log('👤 User:', response.user);
        
        // Track authentication event
        analyticsService.track({
          name: response.isNewUser ? 'user_signed_up' : 'user_logged_in',
          properties: {
            auth_provider: 'apple',
            is_new_user: response.isNewUser || false,
            source: 'web',
            login_method: 'oauth'
          }
        });
        
        await onLogin();
        navigate('/');
      } else {
        console.error('❌ Authentication failed:', response.message);
        
        // Track auth failure
        analyticsService.track({
          name: 'auth_failed',
          properties: {
            auth_provider: 'apple',
            error_type: response.code || 'unknown',
            error_message: response.message
          }
        });
        
        setError(response.message || 'Authentication failed');
      }
    } catch (err: any) {
      console.error('💥 Apple sign-in error:', err);
      const errorMessage = err?.message || 'Failed to sign in with Apple. Please try again.';
      
      // Track auth error
      analyticsService.track({
        name: 'auth_failed',
        properties: {
          auth_provider: 'apple',
          error_type: 'exception',
          error_message: errorMessage
        }
      });
      
      setError(errorMessage);
    } finally {
      console.log('🔄 Clearing loading state');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center px-4 relative overflow-hidden">
      {/* Animated blob background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">

        {/* Foreground layer - normal orientation */}
        <motion.svg
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ duration: 1 }}
          className="absolute top-0 left-0 w-full h-full dark:opacity-5"
          preserveAspectRatio="xMidYMid slice"
          viewBox="10 10 80 80"
        >
          <defs>
            <style>{`
              @keyframes rotateSvgGroup {
                0% {
                  transform: rotate(0deg);
                }
                100% {
                  transform: rotate(360deg);
                }
              }
              @keyframes rotate {
                0% {
                  transform: translateX(-10px) rotate(0deg);
                }
                100% {
                  transform: translateX(-10px) rotate(360deg);
                }
              }
                              
              @keyframes rotate2 {
                0% {
                  transform: translateX(10px) rotate(0deg);
                }
                100% {
                  transform: translateX(10px) rotate(360deg);
                }
              }

              .svg-rotate-group {
                animation: rotateSvgGroup 180s linear infinite;
                transform-origin: 50px 50px;
              }

              .out-top {
                animation: rotate 20s linear infinite;
                transform-origin: 13px 25px;
              }
              .in-top {
                animation: rotate 10s linear infinite;
                transform-origin: 13px 25px;
              }
              .out-bottom {
                animation: rotate2 25s linear infinite;
                transform-origin: 84px 93px;
              }
              .in-bottom {
                animation: rotate2 15s linear infinite;
                transform-origin: 84px 93px;
              }
            `}</style>
            
            {/* Gradient definitions with more stops to prevent banding */}
            <linearGradient id="grad-yellow" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#FCB902' }} />
              <stop offset="25%" style={{ stopColor: '#FCC635' }} />
              <stop offset="50%" style={{ stopColor: '#FDD768' }} />
              <stop offset="75%" style={{ stopColor: '#FEEB9B' }} />
              <stop offset="100%" style={{ stopColor: '#FFFFFF' }} />
            </linearGradient>
            <linearGradient id="grad-pink" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#FF00CF' }} />
              <stop offset="25%" style={{ stopColor: '#FF40D9' }} />
              <stop offset="50%" style={{ stopColor: '#FF80E5' }} />
              <stop offset="75%" style={{ stopColor: '#FFB3EF' }} />
              <stop offset="100%" style={{ stopColor: '#FFFFFF' }} />
            </linearGradient>
            <linearGradient id="grad-orange" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#FFA500' }} />
              <stop offset="25%" style={{ stopColor: '#FFB833' }} />
              <stop offset="50%" style={{ stopColor: '#FFCC66' }} />
              <stop offset="75%" style={{ stopColor: '#FFE099' }} />
              <stop offset="100%" style={{ stopColor: '#FFFFFF' }} />
            </linearGradient>
            <linearGradient id="grad-light-pink" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#FF69B4' }} />
              <stop offset="25%" style={{ stopColor: '#FF87C3' }} />
              <stop offset="50%" style={{ stopColor: '#FFA5D2' }} />
              <stop offset="75%" style={{ stopColor: '#FFC3E1' }} />
              <stop offset="100%" style={{ stopColor: '#FFFFFF' }} />
            </linearGradient>
          </defs>
          <g className="svg-rotate-group">
            <path fill="url(#grad-yellow)" className="out-top" d="M37-5C25.1-14.7,5.7-19.1-9.2-10-28.5,1.8-32.7,31.1-19.8,49c15.5,21.5,52.6,22,67.2,2.3C59.4,35,53.7,8.5,37-5Z"/>
            <path fill="url(#grad-pink)" className="in-top" d="M20.6,4.1C11.6,1.5-1.9,2.5-8,11.2-16.3,23.1-8.2,45.6,7.4,50S42.1,38.9,41,24.5C40.2,14.1,29.4,6.6,20.6,4.1Z"/>
            <path fill="url(#grad-orange)" className="out-bottom" d="M105.9,48.6c-12.4-8.2-29.3-4.8-39.4.8-23.4,12.8-37.7,51.9-19.1,74.1s63.9,15.3,76-5.6c7.6-13.3,1.8-31.1-2.3-43.8C117.6,63.3,114.7,54.3,105.9,48.6Z"/>
            <path fill="url(#grad-light-pink)" className="in-bottom" d="M102,67.1c-9.6-6.1-22-3.1-29.5,2-15.4,10.7-19.6,37.5-7.6,47.8s35.9,3.9,44.5-12.5C115.5,92.6,113.9,74.6,102,67.1Z"/>
          </g>
        </motion.svg>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-md w-full space-y-8 relative z-10"
      >
        {/* Logo and header */}
        <div className="text-center">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            className="mx-auto w-24 h-24 mb-6 flex items-center justify-center"
          >
            <img 
              src={beemiLogo} 
              alt="Beemi Logo" 
              className="w-full h-full object-contain"
            />
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            className="mb-2 cursor-default"
          >
            <span 
              className="font-bold lowercase text-5xl"
              style={{
                backgroundImage: 'linear-gradient(270deg, #FF00CF 0%, #FCB902 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              beemi
            </span>
            <span 
              className="font-light uppercase text-4xl"
              style={{ color: '#FF00CF' }}
            >STUDIO</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.3 }}
            className="text-gray-600 dark:text-dark-text-muted"
          >
            Create, manage, and publish your games
          </motion.p>
        </div>

        {/* Login card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="bg-white dark:bg-dark-surface rounded-xl shadow-xl border border-gray-200 dark:border-dark-border"
        >
          <div className="p-8">
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text text-center mb-6">
                  Sign in to your account
                </h2>
                
                {/* Error display */}
                <AnimatePresence>
                  {error && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                      animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
                      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                      transition={{ duration: 0.2 }}
                      className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg overflow-hidden"
                    >
                      <div className="flex items-center">
                        <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mr-2 flex-shrink-0" />
                        <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Google login button */}
                <button
                  onClick={handleGoogleSignIn}
                  disabled={isLoading || !isGoogleLoaded}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg shadow-sm bg-white dark:bg-dark-surface hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-dark-text font-medium transition-colors disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-3 flex-shrink-0" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      <span>Continue with Google</span>
                    </>
                  )}
                </button>
                
                {!isGoogleLoaded && !isLoading && (
                  <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-2 text-sm text-gray-500 dark:text-dark-text-muted text-center"
                  >
                    Loading Google Sign-In...
                  </motion.p>
                )}

                {/* Apple Sign-In Button */}
                {APPLE_CLIENT_ID && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleAppleSignIn}
                    disabled={isLoading}
                    className="w-full mt-3 flex items-center justify-center px-4 py-3 border-2 rounded-lg shadow-sm font-medium transition-all bg-black dark:bg-white text-white dark:text-black border-black dark:border-white hover:bg-gray-900 dark:hover:bg-gray-100 disabled:opacity-50"
                  >
                    <svg className="w-5 h-5 mr-3 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="ml-2">Signing in...</span>
                      </>
                    ) : (
                      'Continue with Apple'
                    )}
                  </motion.button>
                )}
              </div>

              {/* Terms and Privacy */}
              <p className="text-center text-sm text-gray-500 dark:text-dark-text-muted">
                By signing in, you agree to our{' '}
                <button 
                  onClick={() => setShowTermsModal(true)}
                  className="text-primary-600 hover:text-primary-500 underline"
                >
                  Terms of Service
                </button>{' '}
                and{' '}
                <button 
                  onClick={() => setShowPrivacyModal(true)}
                  className="text-primary-600 hover:text-primary-500 underline"
                >
                  Privacy Policy
                </button>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.3 }}
          className="text-center text-sm text-gray-500 dark:text-dark-text-muted"
        >
          <p>© {new Date().getFullYear()} Beemi Studio. All rights reserved.</p>
        </motion.div>
      </motion.div>

      {/* Terms of Service Modal */}
      <TermsOfServiceModal 
        isOpen={showTermsModal} 
        onClose={() => setShowTermsModal(false)} 
      />

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal 
        isOpen={showPrivacyModal} 
        onClose={() => setShowPrivacyModal(false)} 
      />
    </div>
  )
}

export default AuthPage 